// DevicePairing — ECDH key exchange protocol for transferring the master
// encryption key between devices, using Supabase as a temporary relay.
//
// The pairing ceremony:
//   Device A (initiator, has master key):
//     1. Generates ECDH key pair + 8-char Crockford-base32 code
//     2. Stores public key + hashed code in Supabase device_pairing table
//     3. Displays code (and QR) to user
//     4. Polls for Device B's public key
//     5. Derives shared secret, wraps master key, uploads
//
//   Device B (joiner, needs master key):
//     1. User enters 8-char code (or scans QR)
//     2. Generates own ECDH key pair
//     3. Looks up pairing request, submits own public key
//     4. Polls for wrapped master key
//     5. Derives same shared secret, unwraps master key
//     6. Saves to local KeyStorage
//
// Includes a minimal QR code generator (no external dependencies).

import { getSupabaseClient } from '../storage/session.js';
import { downloadBlob } from '../utils/download.js';
import { CryptoEngine } from './crypto-engine.js';
import { KeyStorage } from './key-storage.js';

/**
 * Initiator-side confirmation gate. The settings UI supplies this; it shows the
 * SAS + joiner device name and resolves true (Approve) / false (Reject). The
 * master key is NOT exported, wrapped, or uploaded until this resolves true.
 * The supplier MUST guarantee the promise settles (resolve false on cancel),
 * or `waitForCompletion` parks forever on `await confirm()`.
 */
export type PairingConfirm = (info: {
  sas: string;
  joinerDeviceName: string | null;
}) => Promise<boolean>;

// In-flight pairing ceremonies subscribe to this so a sign-out (which
// invalidates the Supabase session and starts returning 401s on the
// polling SELECTs) aborts the loops cleanly with a friendly message
// instead of throwing an opaque RLS error.
const _activePairingAborts = new Set<() => void>();
if (typeof window !== 'undefined') {
  window.addEventListener('powersync:auth-required', () => {
    for (const fn of _activePairingAborts) {
      try {
        fn();
      } catch {
        /* per-ceremony abort failed; keep going */
      }
    }
    _activePairingAborts.clear();
  });
}

// HKDF salt that binds the ECDH wrapping-key derivation to a specific pairing
// code. Both the initiator and the joiner MUST derive a byte-identical salt or
// the shared wrapping key differs and the master-key unwrap fails, so this
// security-critical derivation lives in exactly one place. Callers pass the
// already-normalised code (see `CryptoEngine.normalisePairingCode`).
function _derivePairingSalt(normalisedCode: string): Promise<Uint8Array<ArrayBuffer>> {
  return CryptoEngine.sha256Bytes(
    new TextEncoder().encode(`ariadnenotes-pairing-salt:${normalisedCode}`)
  );
}

export const DevicePairing = {
  /**
   * Initiate a pairing session (Device A — has the master key). `masterKey` is
   * the AES-GCM master key to share; `userId` the Supabase user ID.
   */
  async initiatePairing(
    masterKey: CryptoKey,
    userId: string
  ): Promise<{
    code: string;
    qrDataUrl: string;
    waitForCompletion: (confirm: PairingConfirm) => Promise<void>;
    cancel: () => void;
  }> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase client not available');

    // Generate ECDH key pair
    const ecdhKeyPair = await CryptoEngine.generateECDHKeyPair();
    const pubB64 = await CryptoEngine.exportPublicKey(ecdhKeyPair.publicKey);

    // Generate pairing code (already normalised by construction)
    const code = CryptoEngine.generatePairingCode();
    const codeHash = await CryptoEngine.hashPairingCode(code);
    // HKDF salt binds the wrapping-key derivation to this specific code.
    // Use the normalised form so the joiner — who may type the code with
    // dashes / mixed case / look-alikes (I/L/O/U) — derives the same
    // salt without us having to normalise twice.
    const normalisedCode = CryptoEngine.normalisePairingCode(code);
    const pairingSalt = await _derivePairingSalt(normalisedCode);

    // Insert pairing request
    const { data: row, error } = await supabase
      .from('device_pairing')
      .insert({
        user_id: userId,
        pairing_code_hash: codeHash,
        initiator_ecdh_public: pubB64,
        status: 'waiting',
      })
      .select('id, expires_at')
      .single();

    if (error) throw new Error(`Failed to create pairing request: ${error.message}`);
    const pairingId = row.id;
    const expiresAtMs = row.expires_at ? Date.parse(row.expires_at) : null;

    // Generate QR code data URL
    const qrDataUrl = _generateQRDataUrl(code);

    let cancelled = false;
    let cancelReason = 'Pairing cancelled';

    function cancel() {
      cancelled = true;
      supabase
        .from('device_pairing')
        .update({ status: 'expired' })
        .eq('id', pairingId)
        .then(({ error } = {} as any) => {
          if (error) console.warn('[pairing] cancel failed:', error);
        });
    }

    // Auto-abort if the user signs out mid-ceremony. Without this the
    // polling loop hits RLS 401s and throws an opaque Supabase error.
    const onAuthLost = () => {
      cancelled = true;
      cancelReason = 'Pairing aborted: signed out before completion';
    };

    async function waitForCompletion(confirm: PairingConfirm) {
      // Register the sign-out abort hook only for the polling window, paired
      // with the finally that removes it — so a caller that shows the QR but
      // never awaits waitForCompletion can't leak this closure into the
      // module-global set. Nothing before the loop reads `cancelled`.
      _activePairingAborts.add(onAuthLost);
      try {
        // Poll for joiner's public key
        for (let i = 0; i < 300; i++) {
          // 300 × 2s = 10 minutes
          if (cancelled) throw new Error(cancelReason);
          if (expiresAtMs && Date.now() > expiresAtMs) {
            throw new Error('Pairing expired');
          }

          await _sleep(2000);
          // Catch a cancel/sign-out that landed during the sleep with the
          // friendly reason, before the row's own 'expired' status (which
          // cancel() also sets) is read back as the less-specific message.
          if (cancelled) throw new Error(cancelReason);

          const { data, error: readErr } = await supabase
            .from('device_pairing')
            .select('joiner_ecdh_public, status, joiner_device_name')
            .eq('id', pairingId)
            .single();

          // A transient read failure (e.g. an access-token refresh landing
          // mid-poll, or a momentary network blip) returns no row — DON'T tear
          // the whole ceremony down over a single bad read; keep polling. Real
          // expiry is still caught by the expiresAtMs guard above and by the
          // row's own 'expired' status on a later successful read.
          if (readErr || !data) continue;
          if (data.status === 'expired') throw new Error('Pairing expired');

          if (data.joiner_ecdh_public && data.status === 'accepted') {
            // A device has joined. Compute the Short Authentication String over
            // BOTH ephemeral pubkeys + the code, and require an explicit human
            // Approve on THIS device before the master key is wrapped/uploaded.
            // Until this gate, the only authenticity control was same-account
            // RLS — anyone who landed a valid session could siphon the key.
            const sas = await CryptoEngine.deriveSAS(
              pubB64,
              data.joiner_ecdh_public,
              normalisedCode
            );
            const approved = await confirm({
              sas,
              joinerDeviceName: data.joiner_device_name ?? null,
            });

            // The user may have cancelled while we awaited their decision.
            if (cancelled) throw new Error(cancelReason);

            if (!approved) {
              await supabase
                .from('device_pairing')
                .update({ status: 'rejected' })
                .eq('id', pairingId);
              throw new Error('Pairing rejected on this device');
            }

            // Approved — only NOW export the raw master key, derive the shared
            // secret, wrap, and upload. The raw bytes never sit in memory while
            // we wait on the human.
            const masterKeyBytes = await CryptoEngine.exportKey(masterKey);
            const joinerPub = await CryptoEngine.importPublicKey(data.joiner_ecdh_public);
            const wrappingKey = await CryptoEngine.deriveWrappingKey(
              ecdhKeyPair.privateKey,
              joinerPub,
              pairingSalt
            );
            const wrappedKey = await CryptoEngine.wrapMasterKey(masterKeyBytes, wrappingKey);
            const { error: updateErr } = await supabase
              .from('device_pairing')
              .update({ encrypted_master_key: wrappedKey, status: 'completed' })
              .eq('id', pairingId);

            if (updateErr) throw new Error(`Failed to upload wrapped key: ${updateErr.message}`);

            return; // Done
          }
        }

        throw new Error('Pairing timed out');
      } finally {
        _activePairingAborts.delete(onAuthLost);
      }
    }

    return { code, qrDataUrl, waitForCompletion, cancel };
  },

  /**
   * Join a pairing session (Device B — needs the master key). `code` is the
   * pairing code; `userId` the Supabase user ID. Resolves with raw master key
   * bytes.
   */
  async joinPairing(
    code: string,
    userId: string,
    onSAS?: (sas: string) => void
  ): Promise<Uint8Array> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase client not available');

    let signedOut = false;
    const onAuthLost = () => {
      signedOut = true;
    };

    const normalisedCode = CryptoEngine.normalisePairingCode(code);
    const codeHash = await CryptoEngine.hashPairingCode(normalisedCode);
    const pairingSalt = await _derivePairingSalt(normalisedCode);

    // Look up pairing request
    const { data: rows, error } = await supabase
      .from('device_pairing')
      .select('*')
      .eq('pairing_code_hash', codeHash)
      .eq('user_id', userId)
      .eq('status', 'waiting')
      .order('created_at', { ascending: false })
      .limit(1);

    if (error) throw new Error(`Lookup failed: ${error.message}`);
    if (!rows || rows.length === 0) throw new Error('Invalid or expired pairing code');

    const pairing = rows[0];
    const expiresAtMs = pairing.expires_at ? Date.parse(pairing.expires_at) : null;
    if (expiresAtMs && Date.now() > expiresAtMs) {
      throw new Error('Pairing expired');
    }

    // Generate own ECDH key pair
    const ecdhKeyPair = await CryptoEngine.generateECDHKeyPair();
    const myPubB64 = await CryptoEngine.exportPublicKey(ecdhKeyPair.publicKey);

    // Compute the SAS (same inputs + fixed order as the initiator: initiator
    // pubkey first) and surface it so the joiner UI can display it for the
    // human to compare against the digits shown on the other device.
    const sas = await CryptoEngine.deriveSAS(
      pairing.initiator_ecdh_public,
      myPubB64,
      normalisedCode
    );
    onSAS?.(sas);

    // Submit our public key + device name (display-only UX on the initiator's
    // approval prompt — the SAS is the security control, the name is untrusted).
    const { error: updateErr } = await supabase
      .from('device_pairing')
      .update({
        joiner_ecdh_public: myPubB64,
        joiner_device_name: KeyStorage.getDeviceName(),
        status: 'accepted',
      })
      .eq('id', pairing.id);

    if (updateErr) throw new Error(`Failed to submit public key: ${updateErr.message}`);

    // Import initiator's public key
    const initiatorPub = await CryptoEngine.importPublicKey(pairing.initiator_ecdh_public);

    // Register the sign-out abort hook only now, paired with the finally that
    // removes it — so the pre-loop lookup/submit throws above (bad code, expiry,
    // submit failure) can't leak this closure into the module-global set. Those
    // paths are single awaits that don't read `signedOut`, so nothing is lost.
    _activePairingAborts.add(onAuthLost);
    try {
      // Poll for the wrapped master key
      for (let i = 0; i < 300; i++) {
        // 300 × 2s = 10 minutes (matches initiator)
        if (signedOut) throw new Error('Pairing aborted: signed out before completion');
        if (expiresAtMs && Date.now() > expiresAtMs) {
          throw new Error('Pairing expired');
        }
        await _sleep(2000);

        const { data, error: readErr } = await supabase
          .from('device_pairing')
          .select('encrypted_master_key, status')
          .eq('id', pairing.id)
          .single();

        // Tolerate a transient read failure (token refresh / network blip) —
        // keep polling rather than aborting on a single empty read. Genuine
        // expiry is caught by expiresAtMs and the 'expired' status below.
        if (readErr || !data) continue;
        if (data.status === 'expired') throw new Error('Pairing expired');
        if (data.status === 'rejected') {
          throw new Error('Pairing was rejected on the other device.');
        }

        if (data.encrypted_master_key && data.status === 'completed') {
          // Atomically claim the row by transitioning status='completed' →
          // 'consumed' and asking Supabase to return the row only if the
          // UPDATE actually applied. If another joiner raced us, the row is
          // already 'consumed' and the .select() returns no rows, so we know
          // not to trust the wrapped key we just downloaded.
          const { data: claimed, error: consumeErr } = await supabase
            .from('device_pairing')
            .update({ status: 'consumed' })
            .eq('id', pairing.id)
            .eq('status', 'completed')
            .select('id')
            .maybeSingle();
          if (consumeErr) {
            console.warn('[pairing] failed to mark pairing consumed:', consumeErr);
            throw new Error(`Pairing failed to commit: ${consumeErr.message}`);
          }
          if (!claimed) {
            // Another device already claimed this pairing — refuse to use the key.
            throw new Error(
              'Pairing was already used by another device. Start a new pairing session.'
            );
          }

          // Derive the same shared secret and unwrap.
          const wrappingKey = await CryptoEngine.deriveWrappingKey(
            ecdhKeyPair.privateKey,
            initiatorPub,
            pairingSalt
          );
          const masterKeyBytes = await CryptoEngine.unwrapMasterKey(
            data.encrypted_master_key,
            wrappingKey
          );

          return masterKeyBytes;
        }
      }

      throw new Error('Pairing timed out waiting for key');
    } finally {
      _activePairingAborts.delete(onAuthLost);
    }
  },

  // ── Device management ──────────────────────────────────────────────────

  /** Register this device in user_devices. */
  async registerDevice(userId: string): Promise<void> {
    const supabase = getSupabaseClient();
    if (!supabase) return;
    const deviceId = KeyStorage.getDeviceId();
    const deviceName = KeyStorage.getDeviceName();
    const { error } = await supabase.from('user_devices').upsert(
      {
        user_id: userId,
        device_id: deviceId,
        device_name: deviceName,
      },
      { onConflict: 'user_id,device_id' }
    );
    if (error) console.warn('[pairing] registerDevice failed:', error);
  },

  /** List all registered devices for the user. */
  async listDevices(userId: string): Promise<any[]> {
    const supabase = getSupabaseClient();
    if (!supabase) return [];
    const { data, error } = await supabase
      .from('user_devices')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: true });
    if (error) {
      console.error('[pairing] listDevices error:', error);
      return [];
    }
    return data || [];
  },

  /** Rename a device. */
  async renameDevice(userId: string, deviceId: string, newName: string): Promise<void> {
    const supabase = getSupabaseClient();
    if (!supabase) return;
    const { error } = await supabase
      .from('user_devices')
      .update({ device_name: newName })
      .eq('user_id', userId)
      .eq('device_id', deviceId);
    if (error) console.warn('[pairing] renameDevice failed:', error);
  },

  /** Remove a device from the registry. */
  async removeDevice(userId: string, deviceId: string): Promise<void> {
    const supabase = getSupabaseClient();
    if (!supabase) return;
    await supabase.from('user_devices').delete().eq('user_id', userId).eq('device_id', deviceId);
  },

  // ── Encryption metadata ────────────────────────────────────────────────

  /**
   * True iff this account has been initialised with encryption — i.e. a
   * `user_encryption` row exists. The row is created lazily by
   * `WorkspaceKeys.publishStaticKeypair` on first sign-in. Used by app-init
   * to distinguish "fresh account, generate a master key" from "existing
   * account, fresh device, pair to receive the master key".
   *
   * Throws on errors instead of silently returning false. The caller MUST
   * treat a thrown check as "unknown" — auto-provisioning a new master key
   * when this is uncertain is destructive: it overwrites the live
   * user_encryption row and orphans every other device that holds the
   * actual master key.
   */
  async hasEncryptionRow(userId: string): Promise<boolean> {
    const supabase = getSupabaseClient();
    if (!supabase) {
      const err = new Error('Supabase client not available') as Error & { code?: string };
      err.code = 'SUPABASE_UNAVAILABLE';
      throw err;
    }
    const { data, error } = await supabase
      .from('user_encryption')
      .select('user_id')
      .eq('user_id', userId)
      .maybeSingle();
    if (error) {
      const wrapped = new Error(`Could not verify encryption row: ${error.message}`) as Error & {
        code?: string;
        cause?: unknown;
      };
      wrapped.code = 'ENCRYPTION_ROW_CHECK_FAILED';
      wrapped.cause = error;
      throw wrapped;
    }
    return data !== null;
  },

  // ── Key backup helpers ─────────────────────────────────────────────────

  /**
   * Export the master key as a passphrase-protected backup file.
   * Triggers a browser download of a JSON file.
   */
  async exportKeyBackup(masterKey: CryptoKey, passphrase: string): Promise<void> {
    const rawBytes = await CryptoEngine.exportKey(masterKey);
    const backup = await CryptoEngine.exportKeyBackup(rawBytes, passphrase);
    const json = JSON.stringify(backup, null, 2);
    downloadBlob(json, 'ariadnenotes-key-backup.json', { mimeType: 'application/json' });
  },

  /** Import a master key from a backup file. Resolves with raw master key bytes. */
  async importKeyBackup(file: File, passphrase: string): Promise<Uint8Array> {
    const text = await file.text();
    const backup = JSON.parse(text);
    if (backup.version !== 1) throw new Error('Unsupported backup version');
    return CryptoEngine.importKeyBackup(backup, passphrase);
  },
};

function _sleep(ms: number): Promise<void> {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

// ── Minimal QR Code Generator ────────────────────────────────────────────
// Generates a QR code as a data URL (PNG via canvas).
// Supports numeric mode for 6-digit codes (very small QR).
// Based on the QR code specification with simplified encoding.

/** Generate a QR code as a PNG data URL, or '' if the text can't be encoded. */
function _generateQRDataUrl(text: string): string {
  const modules = _generateQRModules(text);
  if (!modules) return '';
  const size = modules.length;
  const scale = 6;
  const border = 4;
  const imgSize = (size + border * 2) * scale;

  const canvas = document.createElement('canvas');
  canvas.width = imgSize;
  canvas.height = imgSize;
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

  // White background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, imgSize, imgSize);

  // Draw modules
  ctx.fillStyle = '#000000';
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (modules[y][x]) {
        ctx.fillRect((x + border) * scale, (y + border) * scale, scale, scale);
      }
    }
  }

  return canvas.toDataURL('image/png');
}

// Minimal QR code encoder — Version 1, Error Correction Level L, Byte mode.
// Sufficient for encoding 6-digit pairing codes.

/** Build the 21×21 module grid for `text`, or null if too long. */
function _generateQRModules(text: string): boolean[][] | null {
  // For simplicity and reliability, use a lookup-table approach for
  // the specific case of short numeric strings (up to 10 chars).
  // This implements a Version 1 (21×21) QR code with EC level M.

  const data = _encodeQRData(text);
  if (!data) return null;

  const size = 21; // Version 1
  const modules = Array.from({ length: size }, () => Array(size).fill(false));
  const reserved = Array.from({ length: size }, () => Array(size).fill(false));

  // Place finder patterns
  _placeFinderPattern(modules, reserved, 0, 0);
  _placeFinderPattern(modules, reserved, size - 7, 0);
  _placeFinderPattern(modules, reserved, 0, size - 7);

  // Place timing patterns
  for (let i = 8; i < size - 8; i++) {
    modules[6][i] = i % 2 === 0;
    reserved[6][i] = true;
    modules[i][6] = i % 2 === 0;
    reserved[i][6] = true;
  }

  // Dark module
  modules[size - 8][8] = true;
  reserved[size - 8][8] = true;

  // Reserve format info areas
  for (let i = 0; i < 9; i++) {
    reserved[8][i] = true;
    reserved[i][8] = true;
  }
  for (let i = 0; i < 8; i++) {
    reserved[8][size - 1 - i] = true;
    reserved[size - 1 - i][8] = true;
  }

  // Place data bits
  _placeDataBits(modules, reserved, data, size);

  // Apply mask pattern 0 (checkerboard: (row + col) % 2 === 0)
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (!reserved[y][x] && (y + x) % 2 === 0) {
        modules[y][x] = !modules[y][x];
      }
    }
  }

  // Place format info (mask 0, EC level M = 0)
  // Format info bits for M-0: 101010000010010
  const formatBits = [1, 0, 1, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0];
  _placeFormatInfo(modules, formatBits, size);

  return modules;
}

/** Encode `text` into the data+EC bit stream, or null if too long. */
function _encodeQRData(text: string): number[] | null {
  // Byte mode encoding for Version 1-M (max 14 bytes)
  const bytes = new TextEncoder().encode(text);
  if (bytes.length > 14) return null;

  const bits: number[] = [];

  // Mode indicator: 0100 (byte mode)
  bits.push(0, 1, 0, 0);

  // Character count (8 bits for Version 1 byte mode)
  for (let i = 7; i >= 0; i--) bits.push((bytes.length >> i) & 1);

  // Data
  for (const b of bytes) {
    for (let i = 7; i >= 0; i--) bits.push((b >> i) & 1);
  }

  // Terminator (up to 4 zeros)
  const totalDataBits = 128; // Version 1-M: 16 data codewords × 8
  const remaining = totalDataBits - bits.length;
  for (let i = 0; i < Math.min(4, remaining); i++) bits.push(0);

  // Pad to byte boundary
  while (bits.length % 8 !== 0) bits.push(0);

  // Pad codewords
  const padBytes = [0xec, 0x11];
  let padIdx = 0;
  while (bits.length < totalDataBits) {
    const pb = padBytes[padIdx % 2];
    for (let i = 7; i >= 0; i--) bits.push((pb >> i) & 1);
    padIdx++;
  }

  // Convert to codewords
  const codewords: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let val = 0;
    for (let j = 0; j < 8; j++) val = (val << 1) | (bits[i + j] || 0);
    codewords.push(val);
  }

  // Generate EC codewords (10 for Version 1-M)
  const ecCodewords = _generateECCodewords(codewords, 10);

  // Combine and convert back to bits
  const allCodewords = [...codewords, ...ecCodewords];
  const allBits: number[] = [];
  for (const cw of allCodewords) {
    for (let i = 7; i >= 0; i--) allBits.push((cw >> i) & 1);
  }

  return allBits;
}

/** Reed-Solomon error-correction codewords over GF(256). */
function _generateECCodewords(data: number[], ecCount: number): number[] {
  // Reed-Solomon error correction using GF(256)
  const gf256Exp = new Array(256);
  const gf256Log = new Array(256);
  let val = 1;
  for (let i = 0; i < 256; i++) {
    gf256Exp[i] = val;
    gf256Log[val] = i;
    val <<= 1;
    if (val >= 256) val ^= 0x11d;
  }
  gf256Log[0] = undefined;

  function gfMul(a: any, b: any) {
    if (a === 0 || b === 0) return 0;
    return gf256Exp[(gf256Log[a] + gf256Log[b]) % 255];
  }

  // Generator polynomial for ecCount EC codewords
  let gen = [1];
  for (let i = 0; i < ecCount; i++) {
    const newGen = new Array(gen.length + 1).fill(0);
    const factor = gf256Exp[i];
    for (let j = 0; j < gen.length; j++) {
      newGen[j] ^= gen[j];
      newGen[j + 1] ^= gfMul(gen[j], factor);
    }
    gen = newGen;
  }

  // Polynomial division
  const result = new Array(ecCount).fill(0);
  const msg = [...data, ...result];
  for (let i = 0; i < data.length; i++) {
    const coef = msg[i];
    if (coef !== 0) {
      for (let j = 0; j < gen.length; j++) {
        msg[i + j] ^= gfMul(gen[j], coef);
      }
    }
  }

  return msg.slice(data.length);
}

function _placeFinderPattern(
  modules: boolean[][],
  reserved: boolean[][],
  row: number,
  col: number
): void {
  for (let dy = -1; dy <= 7; dy++) {
    for (let dx = -1; dx <= 7; dx++) {
      const y = row + dy;
      const x = col + dx;
      if (y < 0 || y >= modules.length || x < 0 || x >= modules.length) continue;
      const inOuter = dy === 0 || dy === 6 || dx === 0 || dx === 6;
      const inInner = dy >= 2 && dy <= 4 && dx >= 2 && dx <= 4;
      const inSep = dy === -1 || dy === 7 || dx === -1 || dx === 7;
      modules[y][x] = !inSep && (inOuter || inInner);
      reserved[y][x] = true;
    }
  }
}

function _placeDataBits(
  modules: boolean[][],
  reserved: boolean[][],
  bits: number[],
  size: number
): void {
  let bitIdx = 0;
  let x = size - 1;
  let upward = true;

  while (x >= 0) {
    if (x === 6) {
      x--;
      continue;
    } // skip timing column

    const startY = upward ? size - 1 : 0;
    const endY = upward ? -1 : size;
    const stepY = upward ? -1 : 1;

    for (let y = startY; y !== endY; y += stepY) {
      for (let dx = 0; dx <= 1; dx++) {
        const col = x - dx;
        if (col < 0) continue;
        if (reserved[y][col]) continue;
        if (bitIdx < bits.length) {
          modules[y][col] = bits[bitIdx] === 1;
          bitIdx++;
        }
      }
    }

    x -= 2;
    upward = !upward;
  }
}

function _placeFormatInfo(modules: boolean[][], bits: number[], size: number): void {
  // Around top-left finder pattern
  const positions0 = [
    [8, 0],
    [8, 1],
    [8, 2],
    [8, 3],
    [8, 4],
    [8, 5],
    [8, 7],
    [8, 8],
    [7, 8],
    [5, 8],
    [4, 8],
    [3, 8],
    [2, 8],
    [1, 8],
    [0, 8],
  ];
  for (let i = 0; i < 15; i++) {
    const [y, x] = positions0[i];
    modules[y][x] = bits[i] === 1;
  }

  // Bottom-left and top-right
  for (let i = 0; i < 7; i++) {
    modules[size - 1 - i][8] = bits[i] === 1;
  }
  for (let i = 7; i < 15; i++) {
    modules[8][size - 15 + i] = bits[i] === 1;
  }
}
